import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { Check, Trash } from 'react-feather';
import { theme } from '../../theme/theme';
import { IconButton } from './';
import { motions } from './motions/motions';

const useCheckInputClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        borderRadius: 5,
        padding: 10,
        background: theme.colors.white,
        boxShadow: '0 1px 1px rgba(0, 0, 0, 0.14)',
        transition: `all ${theme.transitions.fast}s`,
        '&.editing': {
            padding: '20px 10px',
            background: theme.colors.neutral[4],
        },
        '& > span': {
            flex: 1,
            outline: 'none',
            ...theme.typography.content.label.default,
            '&::before': {
                content: '"\u200b"',
            },
        },
    },
    icons: {
        display: 'flex',
        margin: '-10px 0',
    },
});

/**
 * Input with delete and change options.
 *
 * @param props.defaultValue initial input value
 * @param props.editable if the component can be edited
 * @param props.onChange callback triggered when the component changes
 * @param props.onDelete callback triggered when the delete button is clicked
 */
export const CheckInput = (
    props: JSX.IntrinsicElements['div'] & {
        defaultValue?: string;
        editable?: boolean;
        onConfirm?: (value: string) => void;
        onDelete?: () => void;
    },
) => {
    const { defaultValue, editable = false, onConfirm, onDelete, ...rootProps } = props;
    const classes = useCheckInputClasses();
    const span$ = React.useRef<HTMLSpanElement>();
    const [editing, setEditing] = React.useState(false);
    const cls = { editing };

    React.useLayoutEffect(() => {
        if (!span$.current) return;
        span$.current.textContent = props.defaultValue ?? '';
    }, [span$.current, props.defaultValue]);

    return (
        <div
            {...rootProps}
            className={clsx(classes.root, cls, rootProps.className)}
            onClick={event => (setEditing(editable), rootProps.onClick?.(event))}
        >
            <span ref={span$ as any} contentEditable={props.editable && editing} />
            <AnimatePresence>
                {editable && editing && (
                    <motion.div className={classes.icons} {...motions.presence({ slide: 10 })}>
                        <IconButton
                            size='small'
                            onClick={event => {
                                event.stopPropagation();
                                setEditing(false);
                                props.onConfirm?.(span$.current?.textContent ?? '');
                            }}
                        >
                            <Check />
                        </IconButton>
                        <IconButton size='small' onClick={props.onDelete}>
                            <Trash />
                        </IconButton>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
